import {
    useContext,
    useEffect,
    Fragment,
    useRef
} from "react";
import type {
    ReactNode
} from "react";
import {
    useWindowDimensions,
    Platform,
    View
} from "react-native";
import type RootStackParamList from "./type";
import type {
    MenuButton
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitEmbeddedMenu,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitMenu,
    LocaleSwitcher,
    ThemeSwitcher,
    UserShortcut,
    MainHeader,
    SiteLogo,
    Button
} from "ncore-ui-kit";
import {
    NavigationContainer,
    type LinkingOptions,
    getStateFromPath,
    useNavigation
} from "@react-navigation/native";
import {
    GraduationCapIcon,
    ScrollTextIcon,
    ComponentIcon,
    BanknoteIcon,
    BookOpenIcon,
    UserCogIcon,
    ContactIcon,
    ShieldIcon,
    UsersIcon,
    HomeIcon,
    MenuIcon,
    TagsIcon,
    BoxIcon,
    CogIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp,
    createNativeStackNavigator
} from "@react-navigation/native-stack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../contexts/AuthContext";
import RootOrganizations from "../pages/root/views/organizations";
import RootSuperAdmins from "../pages/root/views/superAdmins";
import FinanceCategories from "../pages/financeCategories";
import ForgotPassword from "../pages/forgotPassword";
import ResetPassword from "../pages/resetPassword";
import CharityBoxes from "../pages/charityBoxes";
import Dashboard from "../pages/dashboard";
import Students from "../pages/students";
import Teachers from "../pages/teachers";
import Courses from "../pages/courses";
import Finance from "../pages/finance";
import Landing from "../pages/landing";
import Members from "../pages/members";
import Profile from "../pages/profile";
import Login from "../pages/login";
import Roles from "../pages/roles";
import Users from "../pages/users";
import Logs from "../pages/logs";

const AppStack = createNativeStackNavigator();
const RootStack = createNativeStackNavigator();

const RenderHomeIcon = ({
    color
}: {
    color: keyof NCoreUIKit.IconContentColors;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <HomeIcon
        color={colors.content.icon[color]}
    />;
};

const RenderComponentIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <ComponentIcon
        color={colors.content.icon[color]}
    />;
};

const RenderScrollTextIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <ScrollTextIcon
        color={colors.content.icon[color]}
    />;
};

const RenderSuperAdminIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <UserCogIcon
        color={colors.content.icon[color]}
    />;
};

const RenderFinanceIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <BanknoteIcon
        color={colors.content.icon[color]}
    />;
};

const RenderFinanceCategoriesIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <TagsIcon
        color={colors.content.icon[color]}
    />;
};

const RenderBoxIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <BoxIcon
        color={colors.content.icon[color]}
    />;
};

const RenderCogsIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <CogIcon
        color={colors.content.icon[color]}
    />;
};

const RenderRolesIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <ShieldIcon
        color={colors.content.icon[color]}
    />;
};

const RenderUsersIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <UsersIcon
        color={colors.content.icon[color]}
    />;
};

const RenderMembersIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <ContactIcon
        color={colors.content.icon[color]}
    />;
};

const updateStorage = () => {
    if (Platform.OS === "web") {
        const data = NCoreUIKitEmbeddedMenu.state.data;

        if (data && data.length) {
            const currentMenu = data.find(dI => dI.id === "main-menu");

            if (currentMenu) {
                window.localStorage.setItem("main-menu-status", String(currentMenu.isCollapse));
            }
        }
    }
};

const updateLocaleStorage = () => {
    if (Platform.OS === "web") {
        const localeData = NCoreUIKitLocalize.state;

        if (localeData) {
            window.localStorage.setItem("locale", JSON.stringify(localeData));
        }
    }
};

const updateThemeStorage = () => {
    if (Platform.OS === "web") {
        const themeData = NCoreUIKitTheme.state;

        if (themeData) {
            window.localStorage.setItem("theme", JSON.stringify(themeData));
        }
    }
};

const RenderEducationIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <GraduationCapIcon
        color={colors.content.icon[color]}
    />;
};

const RenderCoursesIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <TagsIcon
        color={colors.content.icon[color]}
    />;
};

const RenderGraduationCapIcon = ({
    color
}: { color: keyof NCoreUIKit.IconContentColors; }) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <BookOpenIcon
        color={colors.content.icon[color]}
    />;
};

const RenderSiteLogo = ({
    isActive
}: {
    isActive: boolean;
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    if (!isActive) {
        return null;
    }

    return <SiteLogo
        title={localize("menu")}
        isShowIcon={false}
        titleColor="mid"
    />;
};

const RenderRightComponent = () => {
    const {
        isAuthenticated,
        logout,
        user
    } = useContext(AuthContext);

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    return <View
        style={[
            stylesheet.rightContainer,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <LocaleSwitcher
            spreadBehaviour="free"
        />
        <ThemeSwitcher
            spreadBehaviour="free"
        />
        {
            !isMobile && (
                <UserShortcut
                    avatarProps={{
                        title: user?.fullName || user?.name_surname || user?.username,
                        imageUrl: user?.profilePicturePath,
                        statusIndicatorType: "online",
                        isStatusIndicator: true,
                        size: "xSmall"
                    }}
                    onLoginPress={async () => {
                        if (isAuthenticated && user?.role === "guest") {
                            await AsyncStorage.setItem("redirect_to_login", "true");
                            await logout();
                        } else {
                            navigation.navigate("Login");
                        }
                    }}
                    title={user?.fullName || user?.name_surname || user?.username}
                    isSessionActive={isAuthenticated && user?.role !== "guest"}
                    onPress={() => {
                        if (isAuthenticated) {
                            navigation.navigate("AuthenticatedApp", {
                                screen: "Profile"
                            });
                        }
                    }}
                    isShowLoginButtonWhenNoSession={true}
                    isWorkWithCoreComplex={false}
                    backgroundColor="transparent"
                    onLogoutPress={async () => {
                        await logout();
                    }}
                    variant="default"
                />)
        }
        {
            isMobile && (
                <Button
                    icon={({
                        color,
                        size
                    }) => <MenuIcon
                        color={colors.content.icon[color]}
                        size={size + 5}
                    />}
                    onPress={() => {
                        NCoreUIKitMenu.open({
                            id: "main-menu"
                        });
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />)
        }
    </View>;
};

const getDynamicMenuButtons = (
    hasFeature: (f: string) => boolean,
    userRole?: string,
    activeOrganizationId?: string | null
): MenuButton[] => {
    const dynamicButtons: MenuButton[] = [];

    if (userRole === "root") {
        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderHomeIcon
                    color={color}
                />;
            },
            title: "menu-dashboard-title",
            redirectMain: "Dashboard",
            isUseLocalize: true
        });

        const settingsSubButtons: MenuButton[] = [
            {
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderComponentIcon
                        color={color}
                    />;
                },
                redirectMain: "RootOrganizations",
                title: "menu-organizations-title",
                isUseLocalize: true
            },
            {
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderSuperAdminIcon
                        color={color}
                    />;
                },
                redirectMain: "RootSuperAdmins",
                title: "menu-superadmins-title",
                isUseLocalize: true
            },
            {
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderScrollTextIcon
                        color={color}
                    />;
                },
                title: "menu-logs-title",
                redirectMain: "Logs",
                isUseLocalize: true
            }
        ];
        if (activeOrganizationId) {
            settingsSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderRolesIcon
                        color={color}
                    />;
                },
                title: "menu-roles-title",
                redirectMain: "Roles",
                isUseLocalize: true
            });

            settingsSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderUsersIcon
                        color={color}
                    />;
                },
                title: "menu-users-title",
                redirectMain: "Users",
                isUseLocalize: true
            });
        }
        dynamicButtons.push({
            id: `settings-${activeOrganizationId ? "with-org" : "no-org"}`,
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderCogsIcon
                    color={color}
                />;
            },
            subButtons: settingsSubButtons,
            title: "menu-settings-title",
            isCollapsible: true,
            isUseLocalize: true
        });

        if (hasFeature("members")) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderMembersIcon
                        color={color}
                    />;
                },
                title: "menu-members-title",
                redirectMain: "Members",
                isUseLocalize: true
            });
        }

        const financeSubButtons: MenuButton[] = [];
        if (hasFeature("finances")) {
            financeSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceIcon
                        color={color}
                    />;
                },
                title: "menu-finance-overview-title",
                redirectMain: "Finance",
                isUseLocalize: true
            });
        }
        if (hasFeature("finance-categories")) {
            financeSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceCategoriesIcon
                        color={color}
                    />;
                },
                title: "menu-finance-categories-title",
                redirectMain: "FinanceCategories",
                isUseLocalize: true
            });
        }
        if (financeSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceIcon
                        color={color}
                    />;
                },
                subButtons: financeSubButtons,
                title: "menu-finance-title",
                isCollapsible: true,
                isUseLocalize: true
            });
        }

        const eduSubButtons: MenuButton[] = [];
        if (hasFeature("courses")) {
            eduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderCoursesIcon
                    color={color}
                />; },
                title: "courses-page-title",
                redirectMain: "Courses",
                isUseLocalize: true
            });
        }
        if (hasFeature("students")) {
            eduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderUsersIcon
                    color={color}
                />; },
                title: "students-page-title",
                redirectMain: "Students",
                isUseLocalize: true
            });
        }
        if (hasFeature("teachers")) {
            eduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderGraduationCapIcon
                    color={color}
                />; },
                title: "teachers-page-title",
                redirectMain: "Teachers",
                isUseLocalize: true
            });
        }
        if (hasFeature("charityBoxes")) {
            eduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderBoxIcon
                    color={color}
                />; },
                title: "menu-charityBoxes-title",
                redirectMain: "CharityBoxes",
                isUseLocalize: true
            });
        }
        if (eduSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderEducationIcon
                    color={color}
                />; },
                title: "menu-education-title",
                subButtons: eduSubButtons,
                isCollapsible: true,
                isUseLocalize: true
            });
        }
    } else if (userRole === "superAdmin") {
        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderHomeIcon
                    color={color}
                />;
            },
            title: "menu-dashboard-title",
            redirectMain: "Dashboard",
            isUseLocalize: true
        });
        const saSettingsSubButtons: MenuButton[] = [];
        if (hasFeature("roles")) {
            saSettingsSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderRolesIcon
                        color={color}
                    />;
                },
                title: "menu-roles-title",
                redirectMain: "Roles",
                isUseLocalize: true
            });
        }
        if (hasFeature("users")) {
            saSettingsSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderUsersIcon
                        color={color}
                    />;
                },
                title: "menu-users-title",
                redirectMain: "Users",
                isUseLocalize: true
            });
        }
        if (saSettingsSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderCogsIcon
                        color={color}
                    />;
                },
                subButtons: saSettingsSubButtons,
                title: "menu-settings-title",
                isCollapsible: true,
                isUseLocalize: true
            });
        }

        if (hasFeature("members")) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderMembersIcon
                        color={color}
                    />;
                },
                title: "menu-members-title",
                redirectMain: "Members",
                isUseLocalize: true
            });
        }

        const saFinanceSubButtons: MenuButton[] = [];
        if (hasFeature("finances")) {
            saFinanceSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceIcon
                        color={color}
                    />;
                },
                title: "menu-finance-overview-title",
                redirectMain: "Finance",
                isUseLocalize: true
            });
        }
        if (hasFeature("finance-categories")) {
            saFinanceSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceCategoriesIcon
                        color={color}
                    />;
                },
                title: "menu-finance-categories-title",
                redirectMain: "FinanceCategories",
                isUseLocalize: true
            });
        }
        if (saFinanceSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceIcon
                        color={color}
                    />;
                },
                subButtons: saFinanceSubButtons,
                title: "menu-finance-title",
                isCollapsible: true,
                isUseLocalize: true
            });
        }

        const saEduSubButtons: MenuButton[] = [];
        if (hasFeature("courses")) {
            saEduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderCoursesIcon
                    color={color}
                />; },
                title: "courses-page-title",
                redirectMain: "Courses",
                isUseLocalize: true
            });
        }
        if (hasFeature("students")) {
            saEduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderUsersIcon
                    color={color}
                />; },
                title: "students-page-title",
                redirectMain: "Students",
                isUseLocalize: true
            });
        }
        if (hasFeature("teachers")) {
            saEduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderGraduationCapIcon
                    color={color}
                />; },
                title: "teachers-page-title",
                redirectMain: "Teachers",
                isUseLocalize: true
            });
        }
        if (saEduSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderEducationIcon
                    color={color}
                />; },
                title: "menu-education-title",
                subButtons: saEduSubButtons,
                isCollapsible: true,
                isUseLocalize: true
            });
        }
    } else if (userRole === "admin") {
        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderHomeIcon
                    color={color}
                />;
            },
            title: "menu-dashboard-title",
            redirectMain: "Dashboard",
            isUseLocalize: true
        });
        if (hasFeature("members")) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderMembersIcon
                        color={color}
                    />;
                },
                title: "menu-members-title",
                redirectMain: "Members",
                isUseLocalize: true
            });
        }

        const adminFinanceSubButtons: MenuButton[] = [];
        if (hasFeature("finances")) {
            adminFinanceSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceIcon
                        color={color}
                    />;
                },
                title: "menu-finance-overview-title",
                redirectMain: "Finance",
                isUseLocalize: true
            });
        }
        if (hasFeature("finance-categories")) {
            adminFinanceSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceCategoriesIcon
                        color={color}
                    />;
                },
                title: "menu-finance-categories-title",
                redirectMain: "FinanceCategories",
                isUseLocalize: true
            });
        }
        if (adminFinanceSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => {
                    return <RenderFinanceIcon
                        color={color}
                    />;
                },
                subButtons: adminFinanceSubButtons,
                title: "menu-finance-title",
                isCollapsible: true,
                isUseLocalize: true
            });
        }

        const adminEduSubButtons: MenuButton[] = [];
        if (hasFeature("courses")) {
            adminEduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderCoursesIcon
                    color={color}
                />; },
                title: "courses-page-title",
                redirectMain: "Courses",
                isUseLocalize: true
            });
        }
        if (hasFeature("students")) {
            adminEduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderUsersIcon
                    color={color}
                />; },
                title: "students-page-title",
                redirectMain: "Students",
                isUseLocalize: true
            });
        }
        if (hasFeature("teachers")) {
            adminEduSubButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderGraduationCapIcon
                    color={color}
                />; },
                title: "teachers-page-title",
                redirectMain: "Teachers",
                isUseLocalize: true
            });
        }
        if (adminEduSubButtons.length > 0) {
            dynamicButtons.push({
                icon: ({
                    color
                }: { color: keyof NCoreUIKit.IconContentColors }) => { return <RenderEducationIcon
                    color={color}
                />; },
                subButtons: adminEduSubButtons,
                title: "menu-education-title",
                isCollapsible: true,
                isUseLocalize: true
            });
        }
    } else if (userRole === "guest") {
        dynamicButtons.push({
            icon: ({
                color
            }: { color: keyof NCoreUIKit.IconContentColors }) => {
                return <RenderHomeIcon
                    color={color}
                />;
            },
            title: "menu-dashboard-title",
            redirectMain: "Dashboard",
            isUseLocalize: true
        });
    }

    return dynamicButtons;
};

const RootNav = () => {
    const {
        activeOrganizationFeatures,
        activeOrganizationId,
        isAuthenticated,
        loading,
        logout,
        user
    } = useContext(AuthContext);

    const {
        activeTheme
    } = NCoreUIKitTheme.useContext();

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const currentMenuStatus = Platform.OS === "web" ? window.localStorage.getItem("main-menu-status") : "false";

    const hasFeature = (f: string) => user?.role === "root" ? activeOrganizationId !== null : activeOrganizationFeatures.includes(f);

    const prevMenuDepsRef = useRef<string>("");

    useEffect(() => {
        const userRole = user?.role;

        const menuDepsString = JSON.stringify({
            features: activeOrganizationFeatures,
            orgId: activeOrganizationId,
            status: currentMenuStatus,
            locale: activeLocale,
            isMobile: isMobile,
            user: user
        });

        if (prevMenuDepsRef.current === menuDepsString) {
            return;
        }

        prevMenuDepsRef.current = menuDepsString;

        const dynamicButtons = getDynamicMenuButtons(
            hasFeature,
            userRole,
            activeOrganizationId
        );

        const currentMenuData = {
            navigation: navigation as unknown as NCoreUIKit.Navigation,
            isCollapse: currentMenuStatus === "true" ? true : false,
            renderSiteLogo: ((props?: {
                isActive?: boolean;
            }) => <RenderSiteLogo
                isActive={props?.isActive ?? true}
            />) as unknown as () => ReactNode,
            isWorkWithSafeAreaView: false,
            buttons: dynamicButtons,
            isAutoClosed: true,
            id: "main-menu"
        };

        const existingEmbeddedMenu = NCoreUIKitEmbeddedMenu.get({
            id: "main-menu"
        });
        if (existingEmbeddedMenu) {
            NCoreUIKitEmbeddedMenu.update({
                menuData: currentMenuData,
                id: "main-menu"
            });
        } else {
            NCoreUIKitEmbeddedMenu.load(currentMenuData);
        }

        const existingMenu = NCoreUIKitMenu.get({
            id: "main-menu"
        });

        const mobileMenuFooter = () => <UserShortcut
            avatarProps={{
                title: user?.fullName || user?.name_surname || user?.username,
                imageUrl: user?.profilePicturePath,
                statusIndicatorType: "online",
                isStatusIndicator: true,
                size: "xSmall"
            }}
            onLoginPress={async () => {
                NCoreUIKitMenu.close({
                    id: "main-menu"
                });
                if (isAuthenticated && user?.role === "guest") {
                    await AsyncStorage.setItem("redirect_to_login", "true");
                    await logout();
                } else {
                    navigation.navigate("Login");
                }
            }}
            title={user?.fullName || user?.name_surname || user?.username}
            isSessionActive={isAuthenticated && user?.role !== "guest"}
            onPress={() => {
                if (isAuthenticated) {
                    NCoreUIKitMenu.close({
                        id: "main-menu"
                    });
                    navigation.navigate("AuthenticatedApp", {
                        screen: "Profile"
                    });
                }
            }}
            isShowLoginButtonWhenNoSession={true}
            isWorkWithCoreComplex={false}
            onLogoutPress={async () => {
                NCoreUIKitMenu.close({
                    id: "main-menu"
                });
                await logout();
            }}
            isShowLogoutButton={true}
            spreadBehaviour="stretch"
            isShowArrowButton={true}
            backgroundColor="mid"
            variant="drawer"
        />;

        if (existingMenu) {
            NCoreUIKitMenu.update({
                menuData: {
                    ...currentMenuData,
                    renderFooter: mobileMenuFooter as unknown as () => ReactNode,
                    isCollapse: existingMenu.isCollapse,
                    isWorkWithSafeAreaView: true
                },
                id: "main-menu"
            });
        } else {
            NCoreUIKitMenu.load({
                ...currentMenuData,
                renderFooter: mobileMenuFooter as unknown as () => ReactNode,
                isWorkWithSafeAreaView: true,
                isCollapse: false
            });
        }
    }, [
        activeOrganizationFeatures,
        activeOrganizationId,
        currentMenuStatus,
        activeLocale,
        user?.role,
        isMobile,
        localize
    ]);

    useEffect(() => {
        NCoreUIKitLocalize.addEventListener("state", updateLocaleStorage);
        NCoreUIKitEmbeddedMenu.addEventListener("state", updateStorage);
        NCoreUIKitTheme.addEventListener("state", updateThemeStorage);

        return () => {
            NCoreUIKitLocalize.removeEventListener("state");
            NCoreUIKitEmbeddedMenu.removeEventListener("state");
            NCoreUIKitTheme.removeEventListener("state");
        };
    }, []);

    const Pages = () => {
        return <Fragment>
            <RootStack.Screen
                component={RootOrganizations}
                name="RootOrganizations"
            />
            <RootStack.Screen
                component={RootSuperAdmins}
                name="RootSuperAdmins"
            />
            <RootStack.Screen
                component={Dashboard}
                name="Dashboard"
            />
            <RootStack.Screen
                component={Finance}
                name="Finance"
            />
            <RootStack.Screen
                component={FinanceCategories}
                name="FinanceCategories"
            />
            <RootStack.Screen
                component={Roles}
                name="Roles"
            />
            <RootStack.Screen
                component={Users}
                name="Users"
            />
            <RootStack.Screen
                component={Members}
                name="Members"
            />
            <RootStack.Screen
                component={Profile}
                name="Profile"
            />
            <RootStack.Screen
                component={Courses}
                name="Courses"
            />
            <RootStack.Screen
                component={Students}
                name="Students"
            />
            <RootStack.Screen
                component={Teachers}
                name="Teachers"
            />
            <RootStack.Screen
                component={CharityBoxes}
                name="CharityBoxes"
            />
            <RootStack.Screen
                component={Logs}
                name="Logs"
            />
        </Fragment>;
    };

    if (loading) {
        return null;
    }

    if (!isAuthenticated) {
        return <AppStack.Navigator
            initialRouteName="Landing"
            screenOptions={{
                headerShown: false
            }}
            key="unauth"
        >
            <AppStack.Screen
                component={Landing}
                name="Landing"
            />
            <AppStack.Screen
                component={Login}
                name="Login"
            />
            <AppStack.Screen
                component={ForgotPassword}
                name="ForgotPassword"
            />
            <AppStack.Screen
                component={ResetPassword}
                name="ResetPassword"
            />
        </AppStack.Navigator>;
    }

    return <AppStack.Navigator
        initialRouteName="AuthenticatedApp"
        screenOptions={{
            headerShown: false
        }}
        key="auth"
    >
        <AppStack.Screen
            component={Landing}
            name="Landing"
        />
        <AppStack.Screen
            name="AuthenticatedApp"
        >
            {() => <MainHeader
                siteLogoProps={{
                    imageUrl: activeTheme.indexOf("dark") !== -1 ? "https://dergah.app/assets/images/darklogo.png" : "https://dergah.app/assets/images/lightlogo.png",
                    onPress: () => {
                        const state = navigation.getState();

                        let isDashboard = false;

                        if (state) {
                            type RouteNode = {
                                state?: {
                                    routes: RouteNode[];
                                    index?: number;
                                };
                                name: string;
                            };

                            let currentRoute = state.routes[state.index] as RouteNode | undefined;

                            for(; currentRoute && currentRoute.state && typeof currentRoute.state.index === "number";) {
                                currentRoute = currentRoute.state.routes[currentRoute.state.index];
                            }

                            if (currentRoute && currentRoute.name === "Dashboard") {
                                isDashboard = true;
                            }
                        }

                        if (isDashboard) {
                            navigation.navigate("Landing" as never);
                        } else {
                            navigation.navigate("AuthenticatedApp", {
                                screen: "Dashboard"
                            });
                        }
                    },
                    subTitle: localize("app-subtitle"),
                    title: localize("app-name"),
                    imageProps: {
                        resizeMode: "stretch"
                    },
                    isWorkWithAction: true,
                    isWorkWithFlex1: true
                }}
                renderRight={RenderRightComponent}
                isWorkWithSeperator={true}
                isWorkWithSticky={true}
                glassEffect={5}
            >
                {
                    isMobile ?
                        <RootStack.Navigator
                            initialRouteName="Dashboard"
                            screenOptions={{
                                headerShown: false
                            }}
                        >
                            {Pages()}
                        </RootStack.Navigator>
                    :
                        <NCoreUIKitEmbeddedMenu.Render
                            navigation={navigation as unknown as NCoreUIKit.Navigation}
                            id="main-menu"
                        >
                            <RootStack.Navigator
                                initialRouteName="Dashboard"
                                screenOptions={{
                                    headerShown: false
                                }}
                            >
                                {Pages()}
                            </RootStack.Navigator>
                        </NCoreUIKitEmbeddedMenu.Render>
                }
            </MainHeader>
            }
        </AppStack.Screen>
    </AppStack.Navigator>;
};

const Navigation = () => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        isAuthenticated
    } = useContext(AuthContext);

    const getTitle = (routeName: string | undefined) => {
        switch (routeName) {
            case "FinanceCategories":
                return localize("finance-categories-page-title");
            case "RootOrganizations":
                return localize("org-page-title");
            case "RootSuperAdmins":
                return localize("superadmin-page-title");
            case "ForgotPassword":
                return localize("forgot-password-page-title");
            case "ResetPassword":
                return localize("reset-password-page-title");
            case "Dashboard":
                return localize("dashboard-page-title");
            case "Students":
                return localize("students-page-title");
            case "Teachers":
                return localize("teachers-page-title");
            case "CharityBoxes":
                return localize("charityBoxes-page-title");
            case "Courses":
                return localize("courses-page-title");
            case "Finance":
                return localize("finance-page-title");
            case "Members":
                return localize("members-page-title");
            case "Login":
                return localize("login-page-title");
            case "Roles":
                return localize("roles-page-title");
            case "Users":
                return localize("users-page-title");
            case "Logs":
                return localize("logs-page-title");
            default:
                return localize("app-name");
        }
    };

    return <NavigationContainer
        linking={{
            getStateFromPath: (path, options) => {
                const state = getStateFromPath(path.toLowerCase(), options);

                if (!isAuthenticated && state?.routes?.find(r => r.name === "AuthenticatedApp")) {
                    return {
                        routes: [
                            {
                                name: "Login"
                            }
                        ]
                    };
                }

                if (isAuthenticated && state?.routes?.find(r => [
                    "ForgotPassword",
                    "ResetPassword",
                    "Login"
                ].includes(r.name))) {
                    return {
                        routes: [
                            {
                                state: {
                                    routes: [
                                        {
                                            name: "Dashboard"
                                        }
                                    ]
                                },
                                name: "AuthenticatedApp"
                            }
                        ]
                    };
                }

                return state;
            },
            config: {
                screens: {
                    AuthenticatedApp: {
                        screens: {
                            FinanceCategories: "finance-categories",
                            RootOrganizations: "root-organizations",
                            RootSuperAdmins: "root-super-admins",
                            CharityBoxes: "charity-boxes",
                            Dashboard: "dashboard",
                            Students: "students",
                            Teachers: "teachers",
                            Courses: "courses",
                            Finance: "finance",
                            Members: "members",
                            Profile: "profile",
                            Roles: "roles",
                            Users: "users",
                            Logs: "logs"
                        }
                    },
                    ForgotPassword: "forgot-password",
                    ResetPassword: "reset-password",
                    Login: "login",
                    Landing: ""
                },
                initialRouteName: "Landing"
            },
            prefixes: [
                "http://localhost:3000",
                "https://dergah.app"
            ]
        } as LinkingOptions<RootStackParamList>}
        documentTitle={{
            formatter: (options, route) => {
                const title = options?.title ?? getTitle(route?.name);
                const appName = localize("app-name");

                if(title === appName) {
                    return title;
                }

                return `${title} | ${appName}`;
            }
        }}
        key={isAuthenticated ? "auth" : "unauth"}
    >
        <RootNav />
    </NavigationContainer>;
};
export default Navigation;
